import {Columns, X} from 'lucide-react';
import {Button} from '@/shared/ui/button';
import {Checkbox} from '@/shared/ui/checkbox';
import {Popover, PopoverContent, PopoverTrigger} from '@/shared/ui/popover';

export interface ColumnsSelectorOption {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** When provided, a delete button is shown for this option. */
  onDelete?: () => void;
}

interface ColumnsSelectorPopoverProps {
  options: ColumnsSelectorOption[];
  title?: string;
}

const ColumnsSelectorPopover = ({
  options,
  title = 'Toggle Columns',
}: ColumnsSelectorPopoverProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button type="button" variant="outline" className="gap-2">
        <Columns className="h-4 w-4" />
        Columns
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-56 p-0" align="end">
      <div className="p-2 border-b border-border">
        <p className="text-sm font-medium text-foreground">{title}</p>
      </div>
      <div className="p-2 max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-y-auto">
        {options.map((col) => (
          <div
            key={col.id}
            className="flex items-center gap-2 py-2 px-2 rounded-md text-sm hover:bg-muted/50"
          >
            <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
              <Checkbox
                checked={col.checked}
                disabled={col.disabled}
                onCheckedChange={(checked) => col.onCheckedChange(Boolean(checked))}
              />
              <span className="text-foreground truncate">{col.label}</span>
            </label>
            {col.onDelete && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); col.onDelete!(); }}
                className="flex-shrink-0 text-muted-foreground hover:text-destructive rounded p-0.5"
                title={`Remove "${col.label}" column`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

export default ColumnsSelectorPopover;
